import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../context/AuthContext'
import { accountAPI, calendarAPI } from '../../services/api'

const STATUS_LABELS = {
  DRAFT: '초안',
  GENERATING: 'AI 생성 중...',
  GENERATED: '검수 대기',
  CONFIRMED: '확정 완료',
  IMAGE_GENERATING: '이미지 생성 중...',
  COMPLETED: '완료',
}

const STATUS_COLORS = {
  DRAFT: 'bg-slate-500',
  GENERATING: 'bg-amber-500 animate-pulse',
  GENERATED: 'bg-blue-500',
  CONFIRMED: 'bg-green-500',
  IMAGE_GENERATING: 'bg-purple-500 animate-pulse',
  COMPLETED: 'bg-emerald-600',
}

const POST_STATUS_LABELS = {
  EMPTY: '미생성',
  GENERATED: 'AI 생성됨',
  EDITED: '수정됨',
  CONFIRMED: '확정',
  IMAGE_READY: '이미지 완료',
  UPLOADING: '업로드 중...',
  UPLOADED: '업로드 완료',
  UPLOAD_FAILED: '업로드 실패',
  CONVERTED: '변환 완료',
}

function ContentCalendarPage({ accountId: propAccountId, embedded = false, apiOverride = null, readOnly = false }) {
  const { user } = useAuth()
  const api = apiOverride || calendarAPI
  const [account, setAccount] = useState(null)
  const [calendars, setCalendars] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // 모달 상태
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedCalendar, setSelectedCalendar] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)

  // 인라인 편집 상태
  const [expandedPostId, setExpandedPostId] = useState(null)
  const [editingPost, setEditingPost] = useState(null)
  const [postSaving, setPostSaving] = useState(false)

  // 파일 업로드
  const fileInputRef = useRef(null)
  const [uploadTarget, setUploadTarget] = useState(null) // null=새 업로드, {mediaId} = 교체

  // 생성 폼
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    durationWeeks: 4,
    posts: [{ sortOrder: 1, scheduledAt: '', subjectHint: '', moodHint: '' }],
  })

  useEffect(() => {
    if (propAccountId) {
      loadDataByAccountId(propAccountId)
    } else if (user?.username) {
      loadData()
    }
  }, [user, propAccountId])

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 3000)
      return () => clearTimeout(t)
    }
  }, [success])

  const loadDataByAccountId = async (accId) => {
    try {
      setLoading(true)
      setAccount({ id: accId })
      const calRes = await api.list(accId)
      if (calRes.success) setCalendars(calRes.data || [])
    } catch (err) {
      setError('데이터 로딩에 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const loadData = async () => {
    try {
      setLoading(true)
      const accountRes = await accountAPI.getMy()
      if (!accountRes.success || !accountRes.data?.length) {
        setLoading(false)
        return
      }
      const myAccount = accountRes.data.find(a => a.status === 'ACTIVE' && a.platformUserId) || accountRes.data[0]
      setAccount(myAccount)
      accountRes.data = myAccount
      const calRes = await api.list(accountRes.data.id)
      if (calRes.success) setCalendars(calRes.data || [])
    } catch (err) {
      setError('데이터 로딩에 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const showMsg = (msg) => setSuccess(msg)

  // === 캘린더 생성 ===
  const addPostRow = () => {
    setCreateForm(prev => ({
      ...prev,
      posts: [...prev.posts, {
        sortOrder: prev.posts.length + 1,
        scheduledAt: '',
        subjectHint: '',
        moodHint: '',
      }],
    }))
  }

  const removePostRow = (index) => {
    setCreateForm(prev => ({
      ...prev,
      posts: prev.posts.filter((_, i) => i !== index).map((p, i) => ({ ...p, sortOrder: i + 1 })),
    }))
  }

  const updatePostRow = (index, field, value) => {
    setCreateForm(prev => ({
      ...prev,
      posts: prev.posts.map((p, i) => i === index ? { ...p, [field]: value } : p),
    }))
  }

  const handleCreate = async () => {
    if (!createForm.title.trim()) return setError('테마명을 입력해주세요.')
    if (createForm.posts.some(p => !p.subjectHint.trim())) return setError('모든 포스트에 주제를 입력해주세요.')

    try {
      setActionLoading(true)
      const res = await api.create({
        accountId: account.id,
        ...createForm,
      })
      if (res.success) {
        showMsg('캘린더가 생성되었습니다.')
        setShowCreateModal(false)
        setCreateForm({ title: '', description: '', durationWeeks: 4, posts: [{ sortOrder: 1, scheduledAt: '', subjectHint: '', moodHint: '' }] })
        loadData()
      }
    } catch (err) {
      setError('캘린더 생성에 실패했습니다.')
    } finally {
      setActionLoading(false)
    }
  }

  // === 캘린더 상세 ===
  const openDetail = async (calId) => {
    try {
      setActionLoading(true)
      const res = await api.getById(calId)
      if (res.success) {
        setSelectedCalendar(res.data)
        setExpandedPostId(null)
        setEditingPost(null)
        // 모바일(lg 미만)에서만 모달 표시, PC에서는 사이드 패널로 표시
        const isLg = window.matchMedia('(min-width: 1024px)').matches
        if (!isLg) {
          setShowDetailModal(true)
        }
      }
    } catch (err) {
      setError('캘린더 로딩에 실패했습니다.')
    } finally {
      setActionLoading(false)
    }
  }

  const refreshDetail = async () => {
    if (!selectedCalendar) return
    const res = await api.getById(selectedCalendar.id)
    if (res.success) {
      setSelectedCalendar(res.data)
      // 확장된 포스트가 있으면 editingPost도 갱신
      if (expandedPostId) {
        const updatedPost = res.data.posts?.find(p => p.id === expandedPostId)
        if (updatedPost) setEditingPost({ ...updatedPost })
      }
    }
  }

  // === 포스트 확장/축소 ===
  const hasUnsavedChanges = () => {
    if (!editingPost || !selectedCalendar) return false
    const original = selectedCalendar.posts?.find(p => p.id === editingPost.id)
    if (!original) return false
    return ['title', 'caption', 'tags', 'imageDescription', 'imagePrompt'].some(
      key => (editingPost[key] || '') !== (original[key] || '')
    )
  }

  const togglePostExpand = (post) => {
    if (expandedPostId === post.id) {
      // 같은 포스트 클릭 → 축소
      setExpandedPostId(null)
      setEditingPost(null)
    } else {
      // 미저장 변경사항 확인
      if (editingPost && hasUnsavedChanges()) {
        if (!confirm('저장하지 않은 변경사항이 있습니다. 다른 포스트로 전환하시겠습니까?')) return
      }
      setExpandedPostId(post.id)
      setEditingPost({ ...post })
    }
  }

  // === 포스트 저장 ===
  const handleSavePost = async () => {
    if (!editingPost) return
    try {
      setPostSaving(true)
      await api.updatePost(selectedCalendar.id, editingPost.id, {
        title: editingPost.title,
        imageDescription: editingPost.imageDescription,
        imagePrompt: editingPost.imagePrompt,
        caption: editingPost.caption,
        tags: editingPost.tags,
      })
      showMsg('포스트가 저장되었습니다.')
      refreshDetail()
    } catch (err) {
      setError('저장에 실패했습니다.')
    } finally {
      setPostSaving(false)
    }
  }

  // === AI 생성 ===
  const handleGenerate = async () => {
    try {
      setActionLoading(true)
      await api.generate(selectedCalendar.id)
      showMsg('AI 생성이 시작되었습니다. 잠시 후 새로고침해주세요.')
      setTimeout(refreshDetail, 5000)
    } catch (err) {
      setError('AI 생성 요청에 실패했습니다.')
    } finally {
      setActionLoading(false)
    }
  }

  const handleRegeneratePost = async (postId, customHints) => {
    try {
      setActionLoading(true)
      await api.regeneratePost(selectedCalendar.id, postId, customHints || {})
      showMsg('포스트가 재생성되었습니다.')
      await refreshDetail()
    } catch (err) {
      setError('재생성에 실패했습니다.')
    } finally {
      setActionLoading(false)
    }
  }

  // === 확정 ===
  const handleConfirmPost = async (postId) => {
    try {
      setActionLoading(true)
      await api.confirmPost(selectedCalendar.id, postId)
      showMsg('포스트가 확정되었습니다.')
      refreshDetail()
    } catch (err) {
      setError('확정에 실패했습니다.')
    } finally {
      setActionLoading(false)
    }
  }

  const handleConfirmAll = async () => {
    try {
      setActionLoading(true)
      await api.confirmAll(selectedCalendar.id)
      showMsg('전체 포스트가 확정되었습니다.')
      refreshDetail()
    } catch (err) {
      setError('전체 확정에 실패했습니다.')
    } finally {
      setActionLoading(false)
    }
  }

  // === 이미지 생성 ===
  const handleGenerateImages = async () => {
    try {
      setActionLoading(true)
      await api.generateImages(selectedCalendar.id)
      showMsg('이미지 생성이 시작되었습니다.')
      setTimeout(refreshDetail, 8000)
    } catch (err) {
      setError('이미지 생성 요청에 실패했습니다.')
    } finally {
      setActionLoading(false)
    }
  }

  // === Post 변환 ===
  const handleConvert = async () => {
    try {
      setActionLoading(true)
      await api.convert(selectedCalendar.id)
      showMsg('Post 변환이 완료되었습니다. 업로드 대기열에 추가됩니다.')
      refreshDetail()
      loadData()
    } catch (err) {
      setError('Post 변환에 실패했습니다.')
    } finally {
      setActionLoading(false)
    }
  }

  // === 자동 예약 토글 ===
  const handleToggleAutoSchedule = async () => {
    try {
      setActionLoading(true)
      const newValue = !selectedCalendar.autoSchedule
      await api.toggleAutoSchedule(selectedCalendar.id, newValue)
      showMsg(newValue ? '자동 예약이 활성화되었습니다.' : '자동 예약이 비활성화되었습니다.')
      await refreshDetail()
    } catch (err) {
      setError('자동 예약 설정에 실패했습니다.')
    } finally {
      setActionLoading(false)
    }
  }

  // === scheduledAt 업데이트 ===
  const handleUpdateScheduledAt = async (postId, dateTimeStr) => {
    try {
      const scheduledAt = dateTimeStr ? dateTimeStr + ':00' : null
      await api.updatePost(selectedCalendar.id, postId, { scheduledAt })
      await refreshDetail()
    } catch (err) {
      setError('예약 시간 설정에 실패했습니다.')
    }
  }

  // === 캘린더 삭제 ===
  const handleDelete = async (calId) => {
    if (!confirm('캘린더를 삭제하시겠습니까?')) return
    try {
      await api.delete(calId)
      showMsg('캘린더가 삭제되었습니다.')
      setShowDetailModal(false)
      loadData()
    } catch (err) {
      setError('삭제에 실패했습니다.')
    }
  }

  // === 미디어 관리 ===
  const handleAddImage = async (postId, customPrompt) => {
    try {
      setActionLoading(true)
      const body = customPrompt ? { customPrompt } : {}
      await api.generatePostImage(selectedCalendar.id, postId, body)
      showMsg('이미지가 생성되었습니다.')
      await refreshDetail()
    } catch (err) {
      setError('이미지 생성에 실패했습니다.')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteImage = async (postId, imageId) => {
    if (!confirm('이미지를 삭제하시겠습니까?')) return
    try {
      setActionLoading(true)
      await api.deletePostImage(selectedCalendar.id, postId, imageId)
      showMsg('이미지가 삭제되었습니다.')
      await refreshDetail()
    } catch (err) {
      setError('이미지 삭제에 실패했습니다.')
    } finally {
      setActionLoading(false)
    }
  }

  const handleMoveImage = async (postId, mediaList, currentIndex, direction) => {
    const newIndex = currentIndex + direction
    if (newIndex < 0 || newIndex >= mediaList.length) return
    const newOrder = [...mediaList]
    const [moved] = newOrder.splice(currentIndex, 1)
    newOrder.splice(newIndex, 0, moved)
    const imageIds = newOrder.map(m => m.id)
    try {
      setActionLoading(true)
      await api.reorderPostImages(selectedCalendar.id, postId, imageIds)
      await refreshDetail()
    } catch (err) {
      setError('순서 변경에 실패했습니다.')
    } finally {
      setActionLoading(false)
    }
  }

  // === 파일 업로드 ===
  const handleUploadClick = (replaceMediaId = null) => {
    setUploadTarget(replaceMediaId ? { mediaId: replaceMediaId } : null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
      fileInputRef.current.click()
    }
  }

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !editingPost) return

    try {
      setActionLoading(true)
      if (uploadTarget?.mediaId) {
        await api.replacePostMedia(selectedCalendar.id, editingPost.id, uploadTarget.mediaId, file)
        showMsg('미디어가 교체되었습니다.')
      } else {
        await api.uploadPostMedia(selectedCalendar.id, editingPost.id, file)
        showMsg('미디어가 업로드되었습니다.')
      }
      await refreshDetail()
    } catch (err) {
      setError(err.message || '파일 업로드에 실패했습니다.')
    } finally {
      setActionLoading(false)
      setUploadTarget(null)
    }
  }

  const inputClass = 'w-full px-4 py-2.5 bg-slate-50 dark:bg-[#1c1c27] border border-slate-200 dark:border-[#3b3b54] rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none text-slate-900 dark:text-white text-sm'
  const btnPrimary = 'px-5 py-2.5 bg-primary text-white rounded-xl font-bold text-sm hover:opacity-90 transition-opacity disabled:opacity-50'
  const btnSecondary = 'px-4 py-2 bg-slate-100 dark:bg-[#1c1c27] text-slate-700 dark:text-slate-300 rounded-xl text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50'

  if (loading) {
    return <div className="flex items-center justify-center py-20">
      <span className="text-slate-400 dark:text-slate-500">로딩 중...</span>
    </div>
  }

  if (!account) {
    return <div className="flex flex-col items-center justify-center py-20">
      <span className="material-symbols-outlined text-5xl text-slate-300 dark:text-slate-700 mb-3">calendar_month</span>
      <p className="text-slate-500 dark:text-slate-400">연결된 계정이 없습니다. 먼저 인스타그램 계정을 연결해주세요.</p>
    </div>
  }

  // 상세 패널 콘텐츠 (PC 사이드패널 & 모바일 모달 공용)
  const renderDetailContent = () => {
    if (!selectedCalendar) return null

    return (
      <>
        {/* 헤더 */}
        <div className="p-6 border-b border-slate-200 dark:border-[#282839] sticky top-0 bg-white dark:bg-[#13131d] z-10">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">{selectedCalendar.title}</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold text-white ${STATUS_COLORS[selectedCalendar.status]}`}>
                  {STATUS_LABELS[selectedCalendar.status]}
                </span>
                <span className="text-xs text-slate-500">{selectedCalendar.durationWeeks}주 / {selectedCalendar.postCount}개</span>
                {/* 자동 예약 토글 */}
                {!readOnly && (
                  <label className="flex items-center gap-1.5 cursor-pointer ml-2" onClick={e => e.stopPropagation()}>
                    <span className="text-xs text-slate-500">자동 예약</span>
                    <div
                      className={`relative w-9 h-5 rounded-full transition-colors ${selectedCalendar.autoSchedule ? 'bg-blue-500' : 'bg-slate-300 dark:bg-slate-600'}`}
                      onClick={handleToggleAutoSchedule}
                    >
                      <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${selectedCalendar.autoSchedule ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </div>
                  </label>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* 단계별 액션 버튼 (readOnly 모드에서는 숨김) */}
              {!readOnly && (selectedCalendar.status === 'DRAFT' || selectedCalendar.status === 'GENERATED') && (
                <button onClick={handleGenerate} disabled={actionLoading || selectedCalendar.status === 'GENERATING'} className={btnPrimary + ' flex items-center gap-1 text-xs'}>
                  <span className="material-symbols-outlined text-sm">auto_awesome</span>
                  AI 생성
                </button>
              )}
              {!readOnly && selectedCalendar.status === 'GENERATED' && (
                <button onClick={handleConfirmAll} disabled={actionLoading} className={btnPrimary + ' flex items-center gap-1 text-xs'}>
                  <span className="material-symbols-outlined text-sm">check_circle</span>
                  전체 확정
                </button>
              )}
              {!readOnly && selectedCalendar.status === 'CONFIRMED' && (
                <button onClick={handleGenerateImages} disabled={actionLoading} className={btnPrimary + ' flex items-center gap-1 text-xs'}>
                  <span className="material-symbols-outlined text-sm">image</span>
                  이미지 생성
                </button>
              )}
              {!readOnly && selectedCalendar.status === 'COMPLETED' && (
                <button onClick={handleConvert} disabled={actionLoading} className={btnPrimary + ' flex items-center gap-1 text-xs'}>
                  <span className="material-symbols-outlined text-sm">publish</span>
                  Post 변환
                </button>
              )}
              <button onClick={() => refreshDetail()} disabled={actionLoading} className={btnSecondary + ' text-xs'}>
                <span className="material-symbols-outlined text-sm">refresh</span>
              </button>
              {!readOnly && (
                <button onClick={() => handleDelete(selectedCalendar.id)} className="p-2 text-red-400 hover:text-red-600">
                  <span className="material-symbols-outlined text-sm">delete</span>
                </button>
              )}
              <button onClick={() => { setShowDetailModal(false); setSelectedCalendar(null); setExpandedPostId(null); setEditingPost(null) }} className="material-symbols-outlined text-slate-400 hover:text-slate-600">close</button>
            </div>
          </div>
        </div>

        {/* 포스트 목록 (아코디언) */}
        <div className="p-6 space-y-3">
          {selectedCalendar.posts?.map(post => {
            const isExpanded = expandedPostId === post.id

            return (
              <div
                key={post.id}
                className={`bg-slate-50 dark:bg-[#1c1c27] rounded-xl border transition-all ${
                  isExpanded
                    ? 'border-primary/50 shadow-lg shadow-primary/5'
                    : 'border-slate-200 dark:border-[#282839] hover:border-primary/30'
                }`}
              >
                {/* 포스트 헤더 (항상 표시) */}
                <div
                  className="p-4 cursor-pointer"
                  onClick={() => togglePostExpand(post)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      {/* 이미지 미리보기 */}
                      <div className="size-16 rounded-lg bg-slate-200 dark:bg-slate-700 flex-shrink-0 flex items-center justify-center overflow-hidden relative">
                        {post.generatedImageUrl ? (
                          <img src={post.generatedImageUrl} alt="" className="size-full object-cover" />
                        ) : (
                          <span className="material-symbols-outlined text-slate-400 dark:text-slate-600">image</span>
                        )}
                        {post.media?.length > 1 && (
                          <span className="absolute bottom-0.5 right-0.5 px-1 py-0.5 bg-black/60 text-white text-[9px] font-bold rounded flex items-center gap-0.5">
                            <span className="material-symbols-outlined" style={{ fontSize: '10px' }}>photo_library</span>
                            {post.media.length}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold text-primary">#{post.sortOrder}</span>
                          <span className="text-xs text-slate-500 dark:text-slate-400">{post.subjectHint}</span>
                          {post.scheduledAt && (
                            <span className="text-xs text-slate-400">
                              {new Date(post.scheduledAt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric', weekday: 'short' })}
                            </span>
                          )}
                        </div>
                        {post.title && (
                          <h4 className="font-bold text-sm text-slate-900 dark:text-white truncate">{post.title}</h4>
                        )}
                        {post.caption && !isExpanded && (
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">{post.caption}</p>
                        )}
                        {post.tags && !isExpanded && (
                          <p className="text-xs text-primary/70 mt-1 truncate">{post.tags}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold text-white ${
                        post.status === 'UPLOADED' ? 'bg-emerald-500' :
                        post.status === 'UPLOADING' ? 'bg-yellow-500 animate-pulse' :
                        post.status === 'UPLOAD_FAILED' ? 'bg-red-500' :
                        post.status === 'CONFIRMED' || post.status === 'IMAGE_READY' || post.status === 'CONVERTED' ? 'bg-green-500' :
                        post.status === 'GENERATED' || post.status === 'EDITED' ? 'bg-blue-500' : 'bg-slate-400'
                      }`}>
                        {POST_STATUS_LABELS[post.status]}
                      </span>
                      {post.status === 'UPLOAD_FAILED' && post.uploadError && (
                        <span className="text-[10px] text-red-500 max-w-[120px] truncate" title={post.uploadError}>
                          {post.uploadError}
                        </span>
                      )}
                      {!readOnly && (post.status === 'GENERATED' || post.status === 'EDITED') && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleConfirmPost(post.id) }}
                          disabled={actionLoading}
                          title="확정"
                          className="p-1 text-green-500 hover:text-green-600"
                        >
                          <span className="material-symbols-outlined text-base">check_circle</span>
                        </button>
                      )}
                      <span className="material-symbols-outlined text-sm text-slate-400">
                        {isExpanded ? 'expand_less' : 'expand_more'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 확장 영역 (인라인 편집) */}
                {isExpanded && editingPost && (
                  <div className="px-4 pb-4 space-y-4 border-t border-slate-200 dark:border-[#282839] pt-4">

                    {/* 예약 시간 설정 */}
                    <div className="flex items-center gap-3">
                      <label className="text-sm font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1">
                        <span className="material-symbols-outlined text-base">schedule</span>
                        예약 시간
                      </label>
                      <input
                        type="datetime-local"
                        value={editingPost.scheduledAt ? editingPost.scheduledAt.substring(0, 16) : ''}
                        onChange={(e) => handleUpdateScheduledAt(editingPost.id, e.target.value)}
                        className={inputClass + ' w-auto text-xs'}
                        min={new Date().toISOString().substring(0, 16)}
                      />
                      {editingPost.uploadedAt && (
                        <span className="text-xs text-emerald-600">
                          업로드: {new Date(editingPost.uploadedAt).toLocaleString('ko-KR')}
                        </span>
                      )}
                    </div>

                    {/* 미디어 관리 섹션 */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1">
                          <span className="material-symbols-outlined text-base">image</span>
                          미디어 ({editingPost.media?.length || (editingPost.generatedImageUrl ? 1 : 0)}/10)
                        </label>
                        {!readOnly && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleAddImage(editingPost.id)}
                              disabled={actionLoading || (editingPost.media?.length || 0) >= 10}
                              className={btnSecondary + ' flex items-center gap-1 text-xs'}
                            >
                              <span className="material-symbols-outlined text-sm">add_photo_alternate</span>
                              이미지 추가
                            </button>
                            <button
                              onClick={() => handleUploadClick()}
                              disabled={actionLoading || (editingPost.media?.length || 0) >= 10}
                              className={btnSecondary + ' flex items-center gap-1 text-xs'}
                            >
                              <span className="material-symbols-outlined text-sm">upload_file</span>
                              파일 업로드
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-5 gap-2">
                        {(editingPost.media && editingPost.media.length > 0) ? (
                          editingPost.media.map((m, idx) => (
                            <div key={m.id} className="relative group">
                              <div className="aspect-square rounded-lg overflow-hidden border-2 border-slate-200 dark:border-[#282839] hover:border-primary/50 transition-colors">
                                {m.mediaType === 'VIDEO' ? (
                                  <div className="size-full bg-slate-800 flex items-center justify-center">
                                    <span className="material-symbols-outlined text-2xl text-white/70">videocam</span>
                                  </div>
                                ) : (
                                  <img src={m.mediaUrl} alt="" className="size-full object-cover" />
                                )}
                              </div>
                              {idx === 0 && (
                                <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-primary text-white text-[9px] font-bold rounded">
                                  대표
                                </span>
                              )}
                              {m.mediaType === 'VIDEO' && (
                                <span className="absolute top-1 right-1 px-1 py-0.5 bg-purple-500 text-white text-[9px] font-bold rounded">
                                  VIDEO
                                </span>
                              )}
                              {/* 호버 시 컨트롤 (readOnly 모드에서 숨김) */}
                              {!readOnly && (
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1">
                                  {idx > 0 && (
                                    <button
                                      onClick={() => handleMoveImage(editingPost.id, editingPost.media, idx, -1)}
                                      className="p-1 bg-white/80 rounded text-slate-700 hover:bg-white"
                                      title="왼쪽으로"
                                    >
                                      <span className="material-symbols-outlined text-sm">chevron_left</span>
                                    </button>
                                  )}
                                  {idx < editingPost.media.length - 1 && (
                                    <button
                                      onClick={() => handleMoveImage(editingPost.id, editingPost.media, idx, 1)}
                                      className="p-1 bg-white/80 rounded text-slate-700 hover:bg-white"
                                      title="오른쪽으로"
                                    >
                                      <span className="material-symbols-outlined text-sm">chevron_right</span>
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleUploadClick(m.id)}
                                    className="p-1 bg-blue-500/80 rounded text-white hover:bg-blue-600"
                                    title="교체"
                                  >
                                    <span className="material-symbols-outlined text-sm">sync</span>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteImage(editingPost.id, m.id)}
                                    className="p-1 bg-red-500/80 rounded text-white hover:bg-red-600"
                                    title="삭제"
                                  >
                                    <span className="material-symbols-outlined text-sm">delete</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          ))
                        ) : editingPost.generatedImageUrl ? (
                          <div className="aspect-square rounded-lg overflow-hidden border-2 border-slate-200 dark:border-[#282839]">
                            <img src={editingPost.generatedImageUrl} alt="" className="size-full object-cover" />
                          </div>
                        ) : (
                          <div className="col-span-5 py-4 text-center text-slate-400 dark:text-slate-600 text-sm">
                            아직 생성된 이미지가 없습니다
                          </div>
                        )}
                      </div>
                    </div>

                    {/* AI 재생성 섹션 (readOnly 모드에서 숨김) */}
                    {!readOnly && post.status !== 'EMPTY' && post.status !== 'CONVERTED' && (
                      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-sm text-amber-600 dark:text-amber-400">auto_awesome</span>
                          <span className="text-xs font-bold text-amber-700 dark:text-amber-400">AI 재생성</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[11px] text-amber-600 dark:text-amber-500 mb-0.5">주제</label>
                            <input
                              className={inputClass + ' text-xs'}
                              value={editingPost.subjectHint || ''}
                              onChange={e => setEditingPost(p => ({ ...p, subjectHint: e.target.value }))}
                              placeholder="예: 프랑스 - 크루아상"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] text-amber-600 dark:text-amber-500 mb-0.5">무드/스타일</label>
                            <input
                              className={inputClass + ' text-xs'}
                              value={editingPost.moodHint || ''}
                              onChange={e => setEditingPost(p => ({ ...p, moodHint: e.target.value }))}
                              placeholder="예: 모던, 미니멀"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <button
                            onClick={() => handleRegeneratePost(editingPost.id, {
                              subjectHint: editingPost.subjectHint,
                              moodHint: editingPost.moodHint
                            })}
                            disabled={actionLoading}
                            className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1"
                          >
                            <span className={`material-symbols-outlined text-sm ${actionLoading ? 'animate-spin' : ''}`}>
                              {actionLoading ? 'progress_activity' : 'autorenew'}
                            </span>
                            {actionLoading ? '생성 중...' : '이 힌트로 재생성'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* 콘텐츠 편집 섹션 */}
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">제목</label>
                        <input className={inputClass} value={editingPost.title || ''} onChange={e => setEditingPost(p => ({ ...p, title: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">이미지 설명 (한글)</label>
                        <textarea className={inputClass + ' h-16 resize-none'} value={editingPost.imageDescription || ''} onChange={e => setEditingPost(p => ({ ...p, imageDescription: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">이미지 프롬프트 (영문)</label>
                        <textarea className={inputClass + ' h-20 resize-none'} value={editingPost.imagePrompt || ''} onChange={e => setEditingPost(p => ({ ...p, imagePrompt: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">캡션</label>
                        <textarea className={inputClass + ' h-28 resize-none'} value={editingPost.caption || ''} onChange={e => setEditingPost(p => ({ ...p, caption: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">태그</label>
                        <input className={inputClass} value={editingPost.tags || ''} onChange={e => setEditingPost(p => ({ ...p, tags: e.target.value }))} />
                      </div>
                    </div>

                    {/* 저장/취소 버튼 */}
                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        onClick={() => { setExpandedPostId(null); setEditingPost(null) }}
                        className={btnSecondary + ' text-xs'}
                      >
                        취소
                      </button>
                      <button
                        onClick={handleSavePost}
                        disabled={postSaving}
                        className={btnPrimary + ' text-xs'}
                      >
                        {postSaving ? '저장 중...' : '저장'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </>
    )
  }

  return (
    <div className="space-y-6">
      {/* 숨겨진 파일 입력 */}
      <input
        type="file"
        ref={fileInputRef}
        hidden
        accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm"
        onChange={handleFileSelected}
      />

      {/* 헤더 */}
      <div className="flex items-center justify-between">
        {!embedded && (
          <div>
            <h2 className="text-2xl font-black text-slate-900 dark:text-white">콘텐츠 캘린더</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">테마 기반 인스타그램 피드를 AI로 자동 생성합니다</p>
          </div>
        )}
        {!readOnly && (
          <button onClick={() => setShowCreateModal(true)} className={btnPrimary + ' flex items-center gap-2'}>
            <span className="material-symbols-outlined text-base">add</span>
            새 캘린더
          </button>
        )}
      </div>

      {/* 알림 */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl text-red-600 dark:text-red-400 text-sm flex justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="material-symbols-outlined text-base">close</button>
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/30 rounded-xl text-green-600 dark:text-green-400 text-sm">
          {success}
        </div>
      )}

      {/* PC: 캘린더 목록 + 사이드 패널 레이아웃 */}
      <div className="flex gap-6">
        {/* 캘린더 목록 */}
        <div className={`${selectedCalendar ? 'hidden lg:block lg:w-[400px] lg:flex-shrink-0' : 'w-full'}`}>
          {calendars.length === 0 ? (
            <div className="p-12 text-center bg-white dark:bg-[#13131d] rounded-2xl border border-slate-200 dark:border-[#282839]">
              <span className="material-symbols-outlined text-5xl text-slate-300 dark:text-slate-700 block mb-3">calendar_month</span>
              <p className="text-slate-500 dark:text-slate-400 mb-4">아직 생성된 캘린더가 없습니다.</p>
              {!readOnly && (
                <button onClick={() => setShowCreateModal(true)} className={btnPrimary}>
                  첫 캘린더 만들기
                </button>
              )}
            </div>
          ) : (
            <div className="grid gap-4">
              {calendars.map(cal => (
                <div
                  key={cal.id}
                  onClick={() => openDetail(cal.id)}
                  className={`p-5 bg-white dark:bg-[#13131d] rounded-2xl border transition-colors cursor-pointer ${
                    selectedCalendar?.id === cal.id
                      ? 'border-primary shadow-md shadow-primary/10'
                      : 'border-slate-200 dark:border-[#282839] hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <span className="material-symbols-outlined text-primary">calendar_month</span>
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 dark:text-white">{cal.title}</h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {cal.durationWeeks}주 / {cal.postCount}개 포스트
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${STATUS_COLORS[cal.status] || 'bg-slate-500'}`}>
                        {STATUS_LABELS[cal.status] || cal.status}
                      </span>
                    </div>
                  </div>
                  {cal.description && (
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 ml-13">{cal.description}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* PC: 우측 사이드 패널 (lg 이상에서만 표시) */}
        {selectedCalendar && (
          <div className="hidden lg:block flex-1 min-w-0">
            <div className="bg-white dark:bg-[#13131d] rounded-2xl border border-slate-200 dark:border-[#282839] max-h-[calc(100vh-200px)] overflow-y-auto sticky top-6">
              {renderDetailContent()}
            </div>
          </div>
        )}
      </div>

      {/* === 생성 모달 === */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#13131d] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-slate-200 dark:border-[#282839]">
            <div className="p-6 border-b border-slate-200 dark:border-[#282839] flex justify-between items-center sticky top-0 bg-white dark:bg-[#13131d] z-10">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">새 콘텐츠 캘린더</h3>
              <button onClick={() => setShowCreateModal(false)} className="material-symbols-outlined text-slate-400 hover:text-slate-600">close</button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">테마명 *</label>
                <input className={inputClass} value={createForm.title} onChange={e => setCreateForm(p => ({ ...p, title: e.target.value }))} placeholder="예: 세계의 빵 여행" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">설명</label>
                <input className={inputClass} value={createForm.description} onChange={e => setCreateForm(p => ({ ...p, description: e.target.value }))} placeholder="캘린더에 대한 간략한 설명" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">기간 (주)</label>
                <input type="number" className={inputClass + ' w-24'} value={createForm.durationWeeks} onChange={e => setCreateForm(p => ({ ...p, durationWeeks: parseInt(e.target.value) || 1 }))} min={1} max={12} />
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-bold text-slate-700 dark:text-slate-300">포스트 목록</label>
                  <button onClick={addPostRow} className={btnSecondary + ' flex items-center gap-1 text-xs'}>
                    <span className="material-symbols-outlined text-sm">add</span>추가
                  </button>
                </div>
                <div className="space-y-3">
                  {createForm.posts.map((post, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 dark:bg-[#1c1c27] rounded-xl border border-slate-200 dark:border-[#282839]">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-bold text-primary">#{post.sortOrder}</span>
                        <input type="datetime-local" className={inputClass + ' flex-1 text-xs'} value={post.scheduledAt} onChange={e => updatePostRow(idx, 'scheduledAt', e.target.value)} />
                        {createForm.posts.length > 1 && (
                          <button onClick={() => removePostRow(idx)} className="material-symbols-outlined text-sm text-red-400 hover:text-red-600">delete</button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input className={inputClass + ' text-xs'} value={post.subjectHint} onChange={e => updatePostRow(idx, 'subjectHint', e.target.value)} placeholder="주제 (예: 프랑스 - 크루아상) *" />
                        <input className={inputClass + ' text-xs'} value={post.moodHint} onChange={e => updatePostRow(idx, 'moodHint', e.target.value)} placeholder="무드 (예: 모던, 미니멀)" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 dark:border-[#282839] flex justify-end gap-3">
              <button onClick={() => setShowCreateModal(false)} className={btnSecondary}>취소</button>
              <button onClick={handleCreate} disabled={actionLoading} className={btnPrimary}>
                {actionLoading ? '생성 중...' : '캘린더 생성'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === 모바일 상세 모달 (lg 미만에서만 표시) === */}
      {showDetailModal && selectedCalendar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 lg:hidden">
          <div className="bg-white dark:bg-[#13131d] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-slate-200 dark:border-[#282839]">
            {renderDetailContent()}
          </div>
        </div>
      )}
    </div>
  )
}

export default ContentCalendarPage
